// src/components/EstatePiecesCard.jsx
// Pièces d'un estate d'artiste : ce qui entoure l'inventaire (structure de l'estate, périmètre et termes de la
// représentation par la galerie, ayant droit, exécuteur, droits, archives, politique d'acheteurs, instructions de
// l'artiste). Une ligne par collection (collection_estate_pieces).
// ARTICHAUT organise l'information : il n'authentifie pas, ne qualifie aucun droit et ne remplace ni le catalogue
// raisonné, ni le contrat, ni le notaire, ni l'avocat. Textes en français, anglais et allemand.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import { estateError, cleanEstateValue } from "../utils/estateValidation";
import EstateLicencesPanel from "./EstateLicencesPanel";
import EstateDecisionsPanel from "./EstateDecisionsPanel";

// Groupes de rubriques : [clé, type]. type : text | area | date | pct | select:<liste>
const GROUPS = [
  ["structure", [
    ["estate_structure", "select:structures"], ["structure_convention", "area"], ["artist_death_date", "date"],
    ["succession_closed_on", "date"], ["interim_administrator", "text"],
  ]],
  ["representation", [
    ["representation_scope", "select:scopes"], ["scope_notes", "area"], ["representation_exclusivity", "select:exclusivities"],
    ["other_galleries", "area"], ["representation_territory", "text"], ["commission_pct", "pct"],
    ["representation_start", "date"], ["representation_end", "date"], ["termination_terms", "area"], ["representation", "area"],
  ]],
  ["rights", [
    ["rights_holder", "text"], ["executor", "text"], ["authentication_body", "text"], ["rights_notes", "area"],
    ["archives_notes", "area"], ["buyer_policy", "area"], ["artist_instructions", "area"],
  ]],
];
const ALL_KEYS = GROUPS.flatMap(([, fields]) => fields.map(([k]) => k));

const TEXT = {
  fr: {
    title: "Pièces de l'estate", save: "Enregistrer", saved: "Enregistré.", error: "Enregistrement impossible : ",
    lead: "Ce qui entoure l'inventaire d'un estate. Notez où se trouvent les pièces et qui les détient : ARTICHAUT n'authentifie pas et ne qualifie aucun droit.",
    errCommission: "La commission doit être un nombre entre 0 et 100.", errDates: "La fin de la représentation ne peut pas précéder son début.",
    notSpecified: "Non précisé",
    groups: { structure: "Structure de l'estate", representation: "Représentation par la galerie", rights: "Droits, archives et instructions" },
    labels: {
      estate_structure: ["Forme de l'estate", ""],
      structure_convention: ["Convention ou statuts applicables", "Intitulé, date, où se trouve le document"],
      artist_death_date: ["Date de décès de l'artiste", ""],
      succession_closed_on: ["Clôture de la succession (si connue)", ""],
      interim_administrator: ["Administrateur provisoire (si désigné)", "Nom et qualité"],
      representation_scope: ["Périmètre de la représentation", ""],
      scope_notes: ["Précisions sur le périmètre", "Par exemple : œuvres détenues par la famille à la date de la convention"],
      representation_exclusivity: ["Exclusivité", ""],
      other_galleries: ["Autres galeries concernées", "Nom, territoire ou partie du stock réparti"],
      representation_territory: ["Territoire", "Par exemple France et Europe"],
      commission_pct: ["Commission de la galerie (%)", "Par exemple 25"],
      representation_start: ["Début de la représentation", ""],
      representation_end: ["Fin ou échéance", ""],
      termination_terms: ["Conditions de fin", "Préavis, reprise du stock non vendu…"],
      representation: ["Contrat de représentation : résumé et lieu du document", "Parties, services couverts : un résumé, le contrat reste chez le juriste"],
      rights_holder: ["Ayant droit (droits d'auteur)", "Nom de la personne ou de la structure"],
      executor: ["Exécuteur ou représentant de l'estate", "Nom et qualité"],
      authentication_body: ["Qui fait autorité pour l'authentification", "Estate, fondation, comité, spécialiste reconnu"],
      rights_notes: ["Droits de reproduction et droit moral", "Licences en cours, demandes d'autorisation : où se trouvent les documents"],
      archives_notes: ["Archives", "Esquisses, lettres, contenu d'atelier : où elles sont et dans quel état"],
      buyer_policy: ["Politique de placement des œuvres", "Profil d'acheteur recherché, accord de prêt pour les expositions, conditions d'annulation prévues"],
      artist_instructions: ["Instructions laissées par l'artiste", "Œuvres invendues, droits, archives : ce qui a été écrit ou dit"],
    },
    options: {
      structures: { foundation: "Fondation", association: "Association", endowment_fund: "Fonds de dotation", joint_ownership: "Indivision encadrée par une convention", heirs: "Héritiers directs", other: "Autre" },
      scopes: { whole_estate: "Tout l'estate", family_held: "Seulement les œuvres détenues par la famille", selected_works: "Une sélection d'œuvres", to_clarify: "À clarifier" },
      exclusivities: { exclusive: "Exclusive", non_exclusive: "Non exclusive", shared: "Partagée avec d'autres galeries", to_clarify: "À clarifier" },
    },
  },
  en: {
    title: "Estate files", save: "Save", saved: "Saved.", error: "Could not save: ",
    lead: "What surrounds an estate's inventory. Note where the files are and who holds them: ARTICHAUT does not authenticate and does not qualify any right.",
    errCommission: "The commission must be a number between 0 and 100.", errDates: "The end of the representation cannot be before its start.",
    notSpecified: "Not specified",
    groups: { structure: "Structure of the estate", representation: "Representation by the gallery", rights: "Rights, archives and instructions" },
    labels: {
      estate_structure: ["Form of the estate", ""],
      structure_convention: ["Agreement or articles that apply", "Title, date, where the document is"],
      artist_death_date: ["Artist's date of death", ""],
      succession_closed_on: ["Closing of the succession (if known)", ""],
      interim_administrator: ["Interim administrator (if appointed)", "Name and capacity"],
      representation_scope: ["Scope of the representation", ""],
      scope_notes: ["Details on the scope", "For example: works held by the family at the date of the agreement"],
      representation_exclusivity: ["Exclusivity", ""],
      other_galleries: ["Other galleries involved", "Name, territory or part of the stock allocated"],
      representation_territory: ["Territory", "For example France and Europe"],
      commission_pct: ["Gallery commission (%)", "For example 25"],
      representation_start: ["Start of the representation", ""],
      representation_end: ["End or expiry", ""],
      termination_terms: ["Termination terms", "Notice period, return of unsold stock…"],
      representation: ["Representation agreement: summary and where the document is", "Parties, services covered: a summary, the contract stays with the lawyer"],
      rights_holder: ["Rights holder (copyright)", "Name of the person or entity"],
      executor: ["Executor or estate representative", "Name and capacity"],
      authentication_body: ["Who is the authentication authority", "Estate, foundation, committee, recognised specialist"],
      rights_notes: ["Reproduction rights and moral rights", "Current licences, permission requests: where the documents are"],
      archives_notes: ["Archives", "Sketches, letters, studio contents: where they are and in what condition"],
      buyer_policy: ["Placement policy for the works", "Buyer profile sought, agreement to lend for exhibitions, rescission terms foreseen"],
      artist_instructions: ["Instructions left by the artist", "Unsold works, rights, archives: what was written or said"],
    },
    options: {
      structures: { foundation: "Foundation", association: "Association", endowment_fund: "Endowment fund", joint_ownership: "Joint ownership under an agreement", heirs: "Direct heirs", other: "Other" },
      scopes: { whole_estate: "The whole estate", family_held: "Only the works held by the family", selected_works: "A selection of works", to_clarify: "To clarify" },
      exclusivities: { exclusive: "Exclusive", non_exclusive: "Non-exclusive", shared: "Shared with other galleries", to_clarify: "To clarify" },
    },
  },
  de: {
    title: "Unterlagen des Nachlasses", save: "Speichern", saved: "Gespeichert.", error: "Speichern nicht möglich: ",
    lead: "Was das Inventar eines Nachlasses umgibt. Notieren Sie, wo die Unterlagen sind und wer sie verwahrt: ARTICHAUT authentifiziert nicht und beurteilt keine Rechte.",
    errCommission: "Die Provision muss eine Zahl zwischen 0 und 100 sein.", errDates: "Das Ende der Vertretung darf nicht vor ihrem Beginn liegen.",
    notSpecified: "Nicht angegeben",
    groups: { structure: "Struktur des Nachlasses", representation: "Vertretung durch die Galerie", rights: "Rechte, Archiv und Anweisungen" },
    labels: {
      estate_structure: ["Form des Nachlasses", ""],
      structure_convention: ["Geltende Vereinbarung oder Satzung", "Bezeichnung, Datum, wo das Dokument liegt"],
      artist_death_date: ["Todesdatum des Künstlers", ""],
      succession_closed_on: ["Abschluss der Erbfolge (falls bekannt)", ""],
      interim_administrator: ["Vorläufiger Verwalter (falls bestellt)", "Name und Funktion"],
      representation_scope: ["Umfang der Vertretung", ""],
      scope_notes: ["Angaben zum Umfang", "Zum Beispiel: Werke im Besitz der Familie zum Zeitpunkt der Vereinbarung"],
      representation_exclusivity: ["Exklusivität", ""],
      other_galleries: ["Weitere beteiligte Galerien", "Name, Gebiet oder aufgeteilter Teil des Bestands"],
      representation_territory: ["Gebiet", "Zum Beispiel Frankreich und Europa"],
      commission_pct: ["Provision der Galerie (%)", "Zum Beispiel 25"],
      representation_start: ["Beginn der Vertretung", ""],
      representation_end: ["Ende oder Ablauf", ""],
      termination_terms: ["Bedingungen für das Ende", "Kündigungsfrist, Rückgabe des unverkauften Bestands …"],
      representation: ["Vertretungsvertrag: Zusammenfassung und Ablageort", "Parteien, abgedeckte Leistungen: eine Zusammenfassung, der Vertrag bleibt beim Juristen"],
      rights_holder: ["Rechteinhaber (Urheberrecht)", "Name der Person oder Einrichtung"],
      executor: ["Testamentsvollstrecker oder Vertreter des Nachlasses", "Name und Funktion"],
      authentication_body: ["Wer für die Authentifizierung maßgeblich ist", "Nachlass, Stiftung, Komitee, anerkannter Fachmann"],
      rights_notes: ["Vervielfältigungsrechte und Urheberpersönlichkeitsrecht", "Laufende Lizenzen, Genehmigungsanfragen: wo die Dokumente liegen"],
      archives_notes: ["Archiv", "Skizzen, Briefe, Atelierinhalt: wo sie sind und in welchem Zustand"],
      buyer_policy: ["Platzierungspolitik für die Werke", "Gesuchtes Käuferprofil, Leihbereitschaft für Ausstellungen, vorgesehene Rückabwicklungsbedingungen"],
      artist_instructions: ["Hinterlassene Anweisungen des Künstlers", "Unverkaufte Werke, Rechte, Archiv: was schriftlich oder mündlich festgelegt wurde"],
    },
    options: {
      structures: { foundation: "Stiftung", association: "Verein", endowment_fund: "Dotationsfonds", joint_ownership: "Gemeinschaftseigentum mit Vereinbarung", heirs: "Direkte Erben", other: "Andere" },
      scopes: { whole_estate: "Der gesamte Nachlass", family_held: "Nur die Werke im Besitz der Familie", selected_works: "Eine Auswahl von Werken", to_clarify: "Zu klären" },
      exclusivities: { exclusive: "Exklusiv", non_exclusive: "Nicht exklusiv", shared: "Mit anderen Galerien geteilt", to_clarify: "Zu klären" },
    },
  },
};

const box = { width: "100%", boxSizing: "border-box", padding: "8px 10px", border: "1px solid #e5e3df", borderRadius: 4, fontSize: 13, fontFamily: "system-ui, sans-serif", background: "#fff" };
const shortKeys = new Set(["pct", "date"]);

export default function EstatePiecesCard({ collectionId, collectionTitle }) {
  const { i18n } = useTranslation();
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const L = TEXT[lang] || TEXT.fr;
  const [vals, setVals] = useState({});
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    supabase.from("collection_estate_pieces").select("*").eq("collection_id", collectionId).maybeSingle()
      .then(({ data }) => { if (alive) setVals(data || {}); });
    return () => { alive = false; };
  }, [collectionId]);

  const save = async () => {
    setMsg("");
    const err = estateError(vals);
    if (err) { setMsg(L.error + (err === "commission" ? L.errCommission : L.errDates)); return; }
    setBusy(true);
    const row = { collection_id: collectionId, updated_at: new Date().toISOString() };
    ALL_KEYS.forEach((k) => { row[k] = cleanEstateValue(k, vals[k]); });
    const { error } = await supabase.from("collection_estate_pieces").upsert(row, { onConflict: "collection_id" });
    setBusy(false);
    setMsg(error ? L.error + error.message : L.saved);
  };

  const renderField = ([key, type]) => {
    const [label, placeholder] = L.labels[key];
    const set = (v) => setVals({ ...vals, [key]: v });
    let input;
    if (type.startsWith("select:")) {
      const list = L.options[type.slice(7)];
      input = (
        <select style={box} value={vals[key] || ""} onChange={(e) => set(e.target.value)}>
          <option value="">{L.notSpecified}</option>
          {Object.entries(list).map(([v, name]) => <option key={v} value={v}>{name}</option>)}
        </select>
      );
    } else if (type === "date") {
      input = <input style={box} type="date" value={vals[key] || ""} onChange={(e) => set(e.target.value)} />;
    } else if (type === "pct") {
      input = <input style={box} type="number" min="0" max="100" step="0.5" inputMode="decimal" value={vals[key] ?? ""} placeholder={placeholder} onChange={(e) => set(e.target.value)} />;
    } else if (type === "area") {
      input = <textarea style={{ ...box, minHeight: 64 }} value={vals[key] || ""} placeholder={placeholder} maxLength={3000} onChange={(e) => set(e.target.value)} />;
    } else {
      input = <input style={box} value={vals[key] || ""} placeholder={placeholder} maxLength={300} onChange={(e) => set(e.target.value)} />;
    }
    return (
      <label key={key} style={{ display: "grid", gap: 3, fontSize: 12.5, color: "#4a453d", maxWidth: shortKeys.has(type) ? 260 : "none" }}>
        <span style={{ fontWeight: 600 }}>{label}</span>
        {input}
      </label>
    );
  };

  return (
    <li style={{ border: "1px solid #e5e3df", borderRadius: 8, background: "#fff", padding: "14px 16px", listStyle: "none" }}>
      <strong style={{ fontSize: 15 }}>{L.title} — {collectionTitle}</strong>
      <div style={{ fontSize: 12.5, color: "#6f695f", margin: "4px 0 10px", lineHeight: 1.45 }}>{L.lead}</div>
      {GROUPS.map(([group, fields]) => (
        <div key={group} style={{ margin: "14px 0 0" }}>
          <div style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8a8378", fontWeight: 700, marginBottom: 8 }}>{L.groups[group]}</div>
          <div style={{ display: "grid", gap: 10 }}>{fields.map(renderField)}</div>
        </div>
      ))}
      <div style={{ marginTop: 14, display: "flex", gap: 10, alignItems: "center" }}>
        <button type="button" onClick={save} disabled={busy}
          style={{ padding: "8px 14px", background: "#1a1a1a", color: "#fff", border: "none", borderRadius: 2, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>{L.save}</button>
        {msg && <span style={{ fontSize: 12.5, color: msg.startsWith(L.error) ? "#8f3a44" : "#2c5f2d" }}>{msg}</span>}
      </div>
      <EstateLicencesPanel collectionId={collectionId} />
      <EstateDecisionsPanel collectionId={collectionId} />
    </li>
  );
}
